package com.taocent.simple.compose.component.blockrichtext.internal.undo

import androidx.compose.ui.text.input.TextFieldValue
import com.taocent.simple.compose.component.blockrichtext.DocumentBlock
import com.taocent.simple.compose.component.blockrichtext.ExperimentalBlockRichTextApi
import com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor
import com.taocent.simple.compose.component.richtext.core.internal.undo.UndoStackCore

@OptIn(ExperimentalBlockRichTextApi::class)
internal class BlockUndoManager(
    private val maxSize: Int = 100,
    private val mergeIntervalMs: Long = 500
) {

    data class BlockSnapshot(
        val blockId: String,
        val value: TextFieldValue
    )

    data class DocumentSnapshot(
        val blocks: List<DocumentBlock>,
        val textValues: Map<String, TextFieldValue>,
        val cursor: EditorCursor,
        val focusedBlockId: String,
        val selectedTableBlockId: String?,
        val navSelectedTableId: String?,
        val navSourceBlockId: String?
    )

    private sealed interface UndoEntry

    private class BlockUndoEntry(
        val blockId: String,
        val before: TextFieldValue,
        val after: TextFieldValue
    ) : UndoEntry

    private class DocumentUndoEntry(
        val before: DocumentSnapshot,
        val after: DocumentSnapshot,
        val mergeKey: String?
    ) : UndoEntry

    sealed interface UndoResult {
        data class Block(val snapshot: BlockSnapshot) : UndoResult
        data class Document(val snapshot: DocumentSnapshot) : UndoResult
    }

    private val undoStackCore = UndoStackCore<UndoEntry>(maxSize, mergeIntervalMs)

    val canUndo: Boolean get() = undoStackCore.canUndo
    val canRedo: Boolean get() = undoStackCore.canRedo

    fun record(blockId: String, before: TextFieldValue, after: TextFieldValue) {
        if (before == after) return

        if (undoStackCore.isInMergeWindow()) {
            val top = undoStackCore.peekUndo()
            if (top is BlockUndoEntry && top.blockId == blockId && top.before != after) {
                undoStackCore.replaceUndoTop(BlockUndoEntry(blockId, top.before, after))
                return
            }
        }

        undoStackCore.pushUndo(BlockUndoEntry(blockId, before, after))
    }

    fun recordDocument(before: DocumentSnapshot, after: DocumentSnapshot, mergeKey: String? = null) {
        if (before == after) return

        if (mergeKey != null && undoStackCore.isInMergeWindow()) {
            val top = undoStackCore.peekUndo()
            if (top is DocumentUndoEntry && top.mergeKey == mergeKey && top.before != after) {
                undoStackCore.replaceUndoTop(DocumentUndoEntry(top.before, after, mergeKey))
                return
            }
        }

        undoStackCore.pushUndo(DocumentUndoEntry(before, after, mergeKey))
    }

    fun undo(): UndoResult? {
        val entry = undoStackCore.popLastUndo() ?: return null
        undoStackCore.pushRedo(entry)
        return entry.toUndoResult()
    }

    fun redo(): UndoResult? {
        val entry = undoStackCore.popLastRedo() ?: return null
        undoStackCore.pushUndo(entry, clearRedo = false)
        return entry.toRedoResult()
    }

    fun clear() {
        undoStackCore.clear()
    }

    private fun UndoEntry.toUndoResult(): UndoResult = when (this) {
        is BlockUndoEntry -> UndoResult.Block(BlockSnapshot(blockId, before))
        is DocumentUndoEntry -> UndoResult.Document(before)
    }

    private fun UndoEntry.toRedoResult(): UndoResult = when (this) {
        is BlockUndoEntry -> UndoResult.Block(BlockSnapshot(blockId, after))
        is DocumentUndoEntry -> UndoResult.Document(after)
    }
}
